/** Default for deployments and clients connected to older servers. */
export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
/** Attachments one message may name. Images share this cap with other files. */
export const ATTACHMENT_MAX_COUNT = 12;
/**
 * How many full-size files one message could carry before the count rose.
 * Twelve attachments are allowed only while their combined bytes stay inside
 * this earlier payload, so a message cannot grow past what a provider request
 * already had to accept.
 */
export const ATTACHMENT_TOTAL_FILE_BUDGET = 4;
export const ARTIFACT_NAME_MAX_LENGTH = 255;
export const ARTIFACT_DESCRIPTION_MAX_LENGTH = 280;
/** Base64 expands payload by 4/3; cap before decode to reject oversize uploads cheaply. */
export const ATTACHMENT_MAX_BASE64_LENGTH = Math.ceil(ATTACHMENT_MAX_BYTES / 3) * 4;

export const ATTACHMENT_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
] as const;

export const ATTACHMENT_AUDIO_MIME_TYPES = [
  "audio/mpeg",
  "audio/wav",
  "audio/ogg",
  "audio/mp4",
] as const;

export const ATTACHMENT_FILE_MIME_TYPES = [
  ...ATTACHMENT_AUDIO_MIME_TYPES,
  "video/mp4",
  "application/pdf",
  "text/plain",
  "text/markdown",
  "text/csv",
  "text/html",
  "application/json",
  "application/zip",
] as const;

export const ATTACHMENT_ALLOWED_MIME_TYPES = [
  ...ATTACHMENT_IMAGE_MIME_TYPES,
  ...ATTACHMENT_FILE_MIME_TYPES,
] as const;

export type AttachmentMimeType = (typeof ATTACHMENT_ALLOWED_MIME_TYPES)[number];

export function isAttachmentImageMimeType(mimeType: string): boolean {
  return (ATTACHMENT_IMAGE_MIME_TYPES as readonly string[]).includes(mimeType);
}

export function isAttachmentAudioMimeType(mimeType: string): boolean {
  return (ATTACHMENT_AUDIO_MIME_TYPES as readonly string[]).includes(mimeType);
}

export function isAllowedAttachmentMimeType(mimeType: string): mimeType is AttachmentMimeType {
  return (ATTACHMENT_ALLOWED_MIME_TYPES as readonly string[]).includes(mimeType);
}

export function validateThreadsSendInput(input: {
  text?: string;
  artifactIds?: string[];
}): boolean {
  const text = input.text?.trim() ?? "";
  const artifactIds = input.artifactIds ?? [];
  return Boolean(text || artifactIds.length);
}

/** Effective per-file upload limit advertised in me/bootstrap. */
export function attachmentMaxBytes(config?: { maxUploadSizeBytes?: number } | null): number {
  return config?.maxUploadSizeBytes ?? ATTACHMENT_MAX_BYTES;
}

/** Combined bytes allowed on one message at the given per-file ceiling. */
export function attachmentMaxTotalBytes(maxFileBytes = ATTACHMENT_MAX_BYTES): number {
  return maxFileBytes * ATTACHMENT_TOTAL_FILE_BUDGET;
}

/**
 * Why a message's attachments cannot be sent, or null when they fit.
 * Callers must surface this instead of dropping the extra files.
 */
export function attachmentSetError(
  count: number,
  totalBytes: number,
  maxFileBytes = ATTACHMENT_MAX_BYTES,
): string | null {
  if (count > ATTACHMENT_MAX_COUNT) {
    return `At most ${ATTACHMENT_MAX_COUNT} attachments per message`;
  }
  const maxTotal = attachmentMaxTotalBytes(maxFileBytes);
  if (totalBytes > maxTotal) {
    return `Attachments exceed the ${attachmentSizeLabel(maxTotal)} total per message`;
  }
  return null;
}

export function attachmentSizeLabel(maxBytes: number): string {
  return `${maxBytes / (1024 * 1024)} MiB`;
}
